'use client'

// import useScrollLock, { useScrollbarOffset } from '@/hooks/useScrollLock'
import { useScrollbarOffset } from '@shared/hooks/useScrollLock'
import { Row, Stack } from '@christiankaindl/lyts'
import { Check, X } from 'lucide-react'
import { useRef, type ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'
import Button from '../Button/Button'
import Text from '../Text/Text'
import * as styles from './Overlay.css'
import {
  Dialog,
  DialogDescription,
  DialogDismiss,
  DialogHeading,
  type DialogProps,
  DialogProvider,
  type DialogProviderProps,
} from '@ariakit/react'

export function Overlay({ children, ...props }: DialogProviderProps) {
  // useScrollLock(open)
  return <DialogProvider {...props}>{children}</DialogProvider>
}

interface OverlayContentProps extends DialogProps {
  title: string
  hideTitle?: boolean
  /**
   * Subtitle/secondary text rendered under the dialog title and inside the
   * StickyHeader on scroll. Strings render as `<Text color='secondary'>`;
   * a ReactNode lets callers render a small icon + label row (used by the
   * task/template modal to flag "Vorlage" / "Wiederkehrende Aufgabe").
   */
  description?: React.ReactNode
  size?: 'full' | 'min' | 'wide' | 'large'
  header?: boolean
  /**
   * Visual variant for the dismiss button. `'done'` shows a checkmark with the
   * brand variant for auto-save modals where closing == confirming.
   */
  dismissVariant?: 'close' | 'done'
  /** Aria-label override; defaults to 'Done' / 'Close' based on `dismissVariant`. */
  dismissLabel?: string
  /** Optional buttons rendered to the left of the dismiss button (e.g. a menu). */
  headerActions?: ReactNode
  children: ReactNode
}
export function OverlayContent({
  title,
  description,
  children,
  header = true,
  ref,
  hideTitle = false,
  size = 'full',
  dismissVariant = 'close',
  dismissLabel,
  headerActions,
  ...props
}: OverlayContentProps) {
  const dialogRef = useRef<null | HTMLDivElement>(null)
  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.5,
    initialInView: false,
    root: dialogRef?.current,
  })

  const headerContent = header ? (
    <>
      <StickyHeader
        text={title}
        subText={description}
        show={!titleInView}
        dismissVariant={dismissVariant}
        dismissLabel={dismissLabel}
        headerActions={headerActions}
      />
      <Row xAlign='space-between' yAlign='flex-start'>
        <Stack gap={0.25}>
          <DialogHeading
            className={styles.title}
            style={
              hideTitle
                ? { height: 1, overflow: 'hidden', opacity: 0, margin: 0 }
                : undefined
            }
            ref={titleRef}
          >
            {title}
          </DialogHeading>
          {description &&
            (typeof description === 'string' ? (
              <DialogDescription
                render={<Text color='secondary'>{description}</Text>}
              />
            ) : (
              <DialogDescription render={description as React.ReactElement} />
            ))}
        </Stack>
      </Row>
    </>
  ) : null

  return (
    <Dialog
      ref={dialogRef}
      className={`${styles.content} ${styles[size]}`}
      style={{
        zIndex: 1000,
        right: useScrollbarOffset(),
      }}
      backdrop={<div className={styles.backdrop} />}
      {...props}
    >
      <Stack className={styles.contentInner} ref={ref}>
        {headerContent}
        {children}
      </Stack>
    </Dialog>
  )
}

function StickyHeader({
  text,
  subText,
  show,
  dismissVariant,
  dismissLabel,
  headerActions,
}: {
  text: string
  subText?: React.ReactNode
  show: boolean
  dismissVariant: 'close' | 'done'
  dismissLabel?: string
  headerActions?: ReactNode
}) {
  const isDone = dismissVariant === 'done'
  const ariaLabel = dismissLabel ?? (isDone ? 'Done' : 'Close')
  return (
    <div className={styles.stickyHeader}>
      <Stack
        gap={0}
        className={styles.bg}
        xAlign='center'
        yAlign='center'
        style={{ opacity: show ? 1 : 0 }}
      >
        <Text as='span' weight={650}>
          {text}
        </Text>
        {subText &&
          (typeof subText === 'string' ? (
            <Text size='small' color='secondary' maxLines={1}>
              {subText}
            </Text>
          ) : (
            subText
          ))}
      </Stack>
      <Row gap={0.25} style={{ position: 'absolute', right: 12, top: 13 }}>
        {headerActions}
        <DialogDismiss
          render={
            <Button
              circular
              square
              size='small'
              variant={isDone ? 'brand' : undefined}
              aria-label={ariaLabel}
            />
          }
        >
          {isDone ? <Check size={16} /> : <X size={16} />}
        </DialogDismiss>
      </Row>
    </div>
  )
}
